import React from "react";
import moment from "moment-timezone";
import { v1 as uuidv1 } from "uuid";

import { fetchApi, withAccessToken } from "../../Network/NetworkService";
import { endpoint, typeStates } from "../../Constants";
import { notif } from "../Ui/Notification";

import "../../Assets/Pages/dermatoSdr.css";
import IconTooltip from "../Ui/IconTooltip";

// Keep in sync with MAX_RANGE_DAYS in DermatoSdrController.js.
const MAX_RANGE_DAYS = 31;

// Same rule as isValidTestEmail in DermatoSdrController.js — basic shape +
// printable ASCII — so the client and server agree on what a valid test
// address is (the server re-checks and aborts on mismatch regardless).
const isValidTestEmail = (s) => {
  const v = (s || "").trim();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) && !/[^\x20-\x7E]/.test(v);
};

class DermatoSdr extends React.Component {
  constructor() {
    super();
    const today = moment().tz("Europe/Paris").format("YYYY-MM-DD");
    this.state = {
      mode: "send", // "send" = first suivi email, "relance" = reminder to non-responders
      startDate: today,
      endDate: today,
      recurrent: "",
      testMode: false,
      testRealUser: false, // false = preview (dummy link), true = real token to an existing user's own email
      testEmail: "",
      users: [],
      loading: false,
      sending: false,
      sendProgress: "0",
      usersLoaded: false,
      sendEvent: null,
      page: 1,
      totalPage: 0,
      countUsers: 0,
      type: "dermatologie",
    };
  }

  componentDidMount() {
    this.fetchUsers();
  }

  componentWillUnmount() {
    if (this.state.sendEvent) {
      this.state.sendEvent.close();
    }
  }

  rangeDays = () =>
    moment(this.state.endDate).diff(moment(this.state.startDate), "days") + 1;

  rangeTooWide = () =>
    this.state.mode === "send" && this.rangeDays() > MAX_RANGE_DAYS;

  fetchUsers = () => {
    if (this.rangeTooWide()) {
      this.setState({ users: [], usersLoaded: true, loading: false });
      return;
    }
    this.setState({ loading: true });
    let url =
      "/dermato-sdr/list?mode=" +
      this.state.mode +
      "&start=" +
      this.state.startDate +
      "&end=" +
      this.state.endDate +
      "&type=" +
      this.state.type +
      "&page=" +
      this.state.page;
    if (this.state.mode === "send" && this.state.recurrent) {
      url += "&recurrent=" + this.state.recurrent;
    }
    fetchApi(url)
      .then((res) => {
        this.setState({
          users: res.users || [],
          totalPage: res.countPages || 0,
          countUsers: res.countUsers || 0,
          usersLoaded: true,
          loading: false,
        });
      })
      .catch(() => {
        notif.addNotification(
          "Erreur",
          "Erreur de récupération des utilisateurs",
          "warning",
        );
        this.setState({ loading: false });
      });
  };

  switchMode = (mode) => {
    if (this.state.mode === mode || this.state.sending) return;
    this.setState(
      { mode, users: [], usersLoaded: false, page: 1 },
      this.fetchUsers,
    );
  };

  handleStartChange = (e) => {
    const startDate = e.target.value;
    // Keep end >= start so the range is always valid.
    const endDate =
      moment(this.state.endDate).isBefore(moment(startDate)) ||
      !this.state.endDate
        ? startDate
        : this.state.endDate;
    this.setState(
      { startDate, endDate, users: [], usersLoaded: false, page: 1 },
      this.fetchUsers,
    );
  };

  handleEndChange = (e) => {
    this.setState(
      { endDate: e.target.value, users: [], usersLoaded: false, page: 1 },
      this.fetchUsers,
    );
  };

  handleTypeChange = (e) => {
    this.setState(
      { type: e.target.value, users: [], usersLoaded: false, page: 1 },
      () => {
        this.fetchUsers();
      },
    );
  };

  handleRecurrentChange = (e) => {
    this.setState(
      { recurrent: e.target.value, users: [], usersLoaded: false, page: 1 },
      () => {
        this.fetchUsers();
      },
    );
  };

  updatePage(page) {
    this.setState({ users: [], page, loading: true }, () => {
      this.fetchUsers();
    });
  }

  sendToAll = () => {
    // A real campaign needs recipients in the list; test mode sends to a single
    // test address and doesn't depend on the cohort, so don't block it on it.
    if (
      this.state.sending ||
      this.rangeTooWide() ||
      (!this.state.testMode && !this.state.users.length)
    )
      return;

    const { testMode, testRealUser, testEmail, countUsers } = this.state;
    const trimmedTest = (testEmail || "").trim();

    if (testMode && !isValidTestEmail(trimmedTest)) {
      notif.addNotification(
        "Erreur",
        "Renseignez une adresse email de test valide (caractères ASCII).",
        "warning",
      );
      return;
    }

    // Confirmation before any real send — guards against an accidental click on
    // a large recipient set.
    let confirmMsg;
    if (testMode && testRealUser) {
      confirmMsg =
        "Test RÉEL : envoyer un vrai email de suivi (connexion réelle) au compte " +
        trimmedTest +
        " ? Utilisez votre propre compte de test.";
    } else if (testMode) {
      confirmMsg =
        "Mode TEST (aperçu) : envoyer 1 email d'exemple à " +
        trimmedTest +
        " ? Lien factice, aucun patient réel contacté.";
    } else {
      confirmMsg =
        "Envoyer l'email " +
        (this.state.mode === "relance" ? "de relance" : "de suivi") +
        " à " +
        countUsers +
        " patient(s) ? De vrais emails seront envoyés.";
    }
    if (!window.confirm(confirmMsg)) return;

    const tokenId = uuidv1();
    const sendEvent = new EventSource(
      withAccessToken(endpoint + "/dermato-sdr/sendProgress?token=" + tokenId),
    );
    this.setState({ sending: true, sendProgress: "0", sendEvent });

    let posted = false;
    let settled = false;
    let fallbackTimer = null;
    let watchdog = null;

    const reset = () => {
      if (settled) return;
      settled = true;
      clearTimeout(fallbackTimer);
      clearTimeout(watchdog);
      this.setState({ sending: false, sendProgress: "0", sendEvent: null });
      sendEvent.close();
    };

    const refreshAfterSend = () => {
      this.setState({ page: 1 }, this.fetchUsers);
    };

    // If the progress stream goes silent (frames buffered by a mobile/cellular
    // proxy), the send still ran server-side and is recorded in the history —
    // don't let the spinner hang. Restart on each frame; fire if idle too long.
    const bumpWatchdog = () => {
      clearTimeout(watchdog);
      watchdog = setTimeout(() => {
        if (settled) return;
        notif.addNotification(
          "Info",
          "Envoi lancé. Consultez l'historique pour le résultat.",
          "info",
        );
        reset();
        refreshAfterSend();
      }, 30000);
    };

    // The POST is what actually triggers the send; the SSE stream is only for
    // live progress (the server produces to Kafka regardless). It used to wait
    // for the stream's first `start` DATA frame before POSTing — but mobile
    // browsers / cellular proxies buffer `text/event-stream`, so that frame was
    // delayed and the click silently did nothing on mobile while working on
    // desktop. Fire on `onopen` (flushes with the headers, before body
    // buffering) with a timeout fallback, so the send can't be silently blocked.
    const triggerSend = () => {
      if (posted) return;
      posted = true;
      clearTimeout(fallbackTimer);
      bumpWatchdog();
      fetchApi("/dermato-sdr/send", {
        method: "POST",
        body: JSON.stringify({
          mode: this.state.mode,
          start: this.state.startDate,
          end: this.state.endDate,
          id: tokenId,
          type: this.state.type,
          recurrent: this.state.recurrent,
          testMode: testMode,
          testRealUser: testMode && testRealUser,
          testEmail: testMode ? trimmedTest : null,
        }),
      }).catch(() => {
        notif.addNotification("Erreur", "Erreur d'envoi", "warning");
        reset();
      });
    };

    fallbackTimer = setTimeout(triggerSend, 5000);
    sendEvent.onopen = triggerSend;

    sendEvent.onmessage = (ev) => {
      if (posted) bumpWatchdog();
      // Some setups deliver the data frame before onopen; treat it identically.
      if (ev.data === "start") {
        triggerSend();
        return;
      }
      if (ev.data.startsWith("fail&")) {
        const parts = ev.data.split("&");
        const total = parseInt(parts[1]) || 0;
        const failCount = parseInt(parts[2]) || 0;
        notif.addNotification(
          "Error",
          total + " envoyés, " + failCount + " échecs",
          "warning",
        );
        reset();
        refreshAfterSend();
      } else if (ev.data.includes("error")) {
        notif.addNotification(
          "Erreur",
          ev.data.split("&")[1] || "Erreur d'envoi",
          "warning",
        );
        reset();
      } else if (ev.data.startsWith("done&")) {
        const parts = ev.data.split("&");
        const total = parseInt(parts[1]) || 0;
        notif.addNotification(
          "Succès",
          testMode
            ? (testRealUser
                ? "Email réel envoyé au compte "
                : "Email de test envoyé à ") + trimmedTest
            : total + " emails de suivi envoyés",
          "success",
        );
        reset();
        refreshAfterSend();
      } else if (ev.data === "empty") {
        notif.addNotification("Info", "Aucun utilisateur à traiter", "info");
        reset();
      } else {
        this.setState({ sendProgress: ev.data });
      }
    };

    sendEvent.onerror = () => {
      // Pre-POST: the stream failed to open — the timeout fallback still fires
      // the send (it doesn't need the stream). Post-POST: a transient stream
      // error must not abort the UI; the send continues server-side and the
      // watchdog clears the spinner. So there's nothing to do here.
    };
  };

  render() {
    const {
      mode,
      startDate,
      endDate,
      users,
      loading,
      sending,
      sendProgress,
      usersLoaded,
      page,
      totalPage,
      countUsers,
      testMode,
      testRealUser,
      testEmail,
    } = this.state;
    const maxDate = moment().tz("Europe/Paris").format("YYYY-MM-DD");
    const isRelance = mode === "relance";
    const tooWide = this.rangeTooWide();

    return (
      <div className="dermato-sdr-container">
        <div className="dermato-sdr-header">
          <h2 className="dermato-sdr-title">Dermato SDR</h2>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              className="dermato-sdr-btn dermatosdr-btn-link"
              onClick={() =>
                this.props.history.push("/dermato-sdr/completions")
              }
            >
              {"Complétions"}
            </button>
            <button
              className="dermato-sdr-btn dermatosdr-btn-link"
              onClick={() => this.props.history.push("/dermato-sdr/historique")}
            >
              {"Historique des envois"}
            </button>
          </div>
        </div>

        <div className="dermato-sdr-tabs">
          <button
            className={
              "dermato-sdr-tab" + (!isRelance ? " dermato-sdr-tab-active" : "")
            }
            onClick={() => this.switchMode("send")}
            disabled={sending}
          >
            {"Envoi suivi"}
          </button>
          <button
            className={
              "dermato-sdr-tab" + (isRelance ? " dermato-sdr-tab-active" : "")
            }
            onClick={() => this.switchMode("relance")}
            disabled={sending}
          >
            {"Relance (non-répondants)"}
          </button>
        </div>

        <div className="dermato-sdr-card">
          <h3 className="dermato-sdr-card-title">
            {isRelance ? "Relance" : "Filtres"}
          </h3>
          {isRelance && (
            <p style={{ color: "#888", fontSize: 14, marginTop: -8 }}>
              {
                "Patients ayant reçu le 1er email il y a 7 jours ou plus et qui n'ont pas répondu au suivi (1 relance maximum)."
              }
            </p>
          )}
          <div className="dermato-sdr-inline">
            {!isRelance && (
              <React.Fragment>
                <div className="dermato-sdr-form-group">
                  <label>Du</label>
                  <input
                    type="date"
                    className="dermato-sdr-input"
                    value={startDate}
                    max={maxDate}
                    onChange={this.handleStartChange}
                  />
                </div>
                <div className="dermato-sdr-form-group">
                  <label>Au</label>
                  <input
                    type="date"
                    className="dermato-sdr-input"
                    value={endDate}
                    min={startDate}
                    max={maxDate}
                    onChange={this.handleEndChange}
                  />
                </div>
                <div className="dermato-sdr-form-group">
                  <label>{"Récurrence"}</label>
                  <select
                    className="dermato-sdr-input"
                    value={this.state.recurrent}
                    onChange={this.handleRecurrentChange}
                  >
                    <option value="">Tous</option>
                    <option value="1">{"Récurrent"}</option>
                    <option value="0">Nouveau</option>
                  </select>
                </div>
                <div className="dermato-sdr-form-group">
                  <label>{"Type"}</label>
                  <select
                    className="dermato-sdr-input"
                    value={this.state.type}
                    onChange={this.handleTypeChange}
                  >
                    {typeStates.map((t) => (
                      <option value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
              </React.Fragment>
            )}
            <div style={{ alignSelf: "flex-end" }}>
              <button
                className="dermato-sdr-btn dermatosdr-btn-primary"
                onClick={this.sendToAll}
                disabled={sending || tooWide || (!testMode && !users.length)}
              >
                {sending
                  ? "Envoi... " + sendProgress + "%"
                  : testMode
                    ? "Envoyer un test"
                    : isRelance
                      ? "Envoyer la relance"
                      : "Envoyer email de suivi"}
              </button>
            </div>
          </div>

          <div className="dermato-sdr-testmode">
            <label className="dermato-sdr-checkbox">
              <input
                type="checkbox"
                checked={testMode}
                disabled={sending}
                onChange={(e) => this.setState({ testMode: e.target.checked })}
              />
              <span>{"Mode test"}</span>
            </label>
            {testMode && (
              <React.Fragment>
                <select
                  className="dermato-sdr-input"
                  style={{ maxWidth: 240 }}
                  value={testRealUser ? "real" : "preview"}
                  disabled={sending}
                  onChange={(e) =>
                    this.setState({ testRealUser: e.target.value === "real" })
                  }
                >
                  <option value="preview">{"Aperçu (lien factice)"}</option>
                  <option value="real">{"Test réel (compte existant)"}</option>
                </select>
                <input
                  type="email"
                  className="dermato-sdr-input"
                  style={{ maxWidth: 260 }}
                  placeholder={
                    testRealUser
                      ? "email d'un utilisateur existant"
                      : "email de test"
                  }
                  value={testEmail}
                  disabled={sending}
                  onChange={(e) => this.setState({ testEmail: e.target.value })}
                />
                <span style={{ color: "#888", fontSize: 13 }}>
                  {testRealUser
                    ? "Vrai email + connexion réelle, envoyé au compte de cette adresse. Rien n'est marqué comme envoyé."
                    : "1 email d'exemple (lien factice) envoyé à cette adresse — aucun patient réel contacté."}
                </span>
              </React.Fragment>
            )}
          </div>
          {tooWide && (
            <p style={{ color: "#c62828", fontSize: 14, marginTop: 8 }}>
              {"Période trop large : maximum " + MAX_RANGE_DAYS + " jours."}
            </p>
          )}
        </div>

        <div className="dermato-sdr-card" style={{ marginTop: 24 }}>
          <h3 className="dermato-sdr-card-title">
            Utilisateurs ({countUsers})
          </h3>

          {loading ? (
            <div className="dermato-sdr-empty">
              <span className="loader" />
            </div>
          ) : usersLoaded && users.length === 0 ? (
            <div className="dermato-sdr-empty">
              {isRelance
                ? "Aucun non-répondant à relancer."
                : "Aucun utilisateur trouvé pour cette date."}
            </div>
          ) : users.length > 0 ? (
            <div>
              <table className="dermato-sdr-table">
                <thead>
                  <tr>
                    <th>Nom</th>
                    <th>Email</th>
                    <th>{"Date dernière requéte"}</th>
                    <th>{"Nb requétes"}</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u._id}>
                      <td>
                        {u.FName} {u.LName}
                      </td>
                      <td>{u.email}</td>
                      <td>
                        {moment(u.latestRequestDate).format("DD/MM/YYYY HH:mm")}
                      </td>
                      <td>{u.requestCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="dermatosdr-pagination">
                <p style={{ margin: 0, color: "#888", fontSize: 14 }}>
                  Page <strong>{page}</strong> / {totalPage}
                </p>
                <div>
                  {page > 1 && (
                    <IconTooltip
                      icon="fa fa-chevron-left"
                      text="Page précédente"
                      onClick={() => this.updatePage(page - 1)}
                    />
                  )}
                  {page < totalPage && (
                    <IconTooltip
                      icon="fa fa-chevron-right"
                      text="Page suivante"
                      onClick={() => this.updatePage(page + 1)}
                    />
                  )}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    );
  }
}

export default DermatoSdr;
